<template>
    <Card
        :size="`col-md-10 col-lg-8 col-xl-6 max-xl-670`"
        :newLayout="true"
        :hideFooter="false"
    >
        <template slot="card-header">
            <slot name="header"></slot>
        </template>
        <template slot="card-body">
            <slot name="body-title"></slot>
            <form
                ref="form"
                method="POST"
                :action="$laraRoute('enrol')"
                @submit.prevent="handleForm"
                aria-label="Set home address"
                id="enrol-form"
                autocomplete="off"
                novalidate
            >
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="country"
                        class="col-md-3"
                        v-translate
                    >Country</label>

                    <div
                        class="col-md-8 col-lg-7"
                        v-if="countries.length"
                    >
                        <CountrySelect
                            :required="true"
                            :options="countries"
                            :hasValue="homeCountry ? homeCountry : false"
                            :setCountry="setCountry"
                            :getIsoCodeFromName="getIsoCodeFromName"
                            :label="'home_country'"
                            :handler="countryHandler"
                        >
                            <template slot-scope="{slotKey}">
                                <span
                                    :data-key="slotKey"
                                    :class="`iti-flag ${slotKey.toLowerCase()}`"
                                ></span>&nbsp;
                            </template>
                        </CountrySelect>
                        <InlineError
                            :errors="errors"
                            type="home_country"
                        />
                    </div>
                    <div
                        class="col-md-8 col-lg-7 loader loader--inline"
                        v-else
                    >
                        <loader />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="home_house_name"
                        class="col-md-3"
                        v-translate
                    >House Name</label>

                    <div class="col-md-8 col-lg-7">
                        <Input
                            id="home_house_name"
                            name="home_house_name"
                            :rule="{ rules: { required: homeHouseNumber ? false : true } }"
                            model="user.home_house_name"
                            :module="namespace"
                            :maxlength="255"
                        />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="home_house_number"
                        class="col-md-3"
                        v-translate
                    >House Number</label>

                    <div class="col-md-8 col-lg-7">
                        <Input
                            id="home_house_number"
                            name="home_house_number"
                            :rule="{ rules: { required: homeHouseName ? false : true } }"
                            model="user.home_house_number"
                            :module="namespace"
                            :maxlength="100"
                        />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="street_address"
                        class="col-md-3"
                        v-translate
                    >Street Address</label>

                    <div class="col-md-8 col-lg-7">
                        <AutoCompleteInput
                            id="street_address"
                            name="home_street"
                            model="user.home_street"
                            :module="namespace"
                            :autocompleteOptions="predictions"
                            :emulatePlaceChange="emulatePlaceChange"
                            :changeHandler="callAutoComplete"
                            :maxlength="255"
                        />
                    </div>
                </div>
                <div
                    class="form-group justify-content-center align-items-start row"
                    v-show="showState"
                >
                    <label
                        for="state"
                        class="col-md-3"
                        v-show="!showCounty"
                        v-translate
                    >State</label>
                    <label
                        for="state"
                        class="col-md-3"
                        v-show="showCounty"
                        v-translate
                    >County</label>

                    <div class="col-md-8 col-lg-7">
                        <Input
                            :rule="showState ? 'required' : ''"
                            validateAs="state"
                            id="state"
                            name="home_state"
                            errorType="home_state"
                            model="user.home_state"
                            :module="namespace"
                            :required="showState"
                            :lazy="false"
                            :maxlength="80"
                        />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="city"
                        class="col-md-3"
                        v-translate
                    >City</label>

                    <div class="col-md-8 col-lg-7">
                        <Input
                            :rule="'required'"
                            validateAs="city"
                            id="city"
                            name="home_city"
                            errorType="home_city"
                            model="user.home_city"
                            :module="namespace"
                            :required="true"
                            :lazy="false"
                            :maxlength="40"
                        />
                    </div>
                </div>
                <div class="form-group justify-content-center align-items-start row">
                    <label
                        for="postcode"
                        class="col-md-3"
                        v-show="showPostcode"
                        v-translate
                    >Postcode</label>
                    <label
                        for="postcode"
                        class="col-md-3"
                        v-show="!showPostcode"
                        v-translate
                    >Zipcode</label>

                    <div class="col-md-8 col-lg-7">
                        <Input
                            :rule="'required'"
                            validateAs="postcode"
                            id="postcode"
                            name="home_postcode"
                            errorType="home_postcode"
                            model="user.home_postcode"
                            :module="namespace"
                            :required="true"
                            :lazy="false"
                            :maxlength="20"
                        />
                    </div>
                </div>
            </form>
        </template>
        <template slot='card-footer'>
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <flash-message></flash-message>
                </div>
            </div>
            <slot
                name="footer"
                :submitForm="submitForm"
                :isValid="isValid"
            ></slot>
        </template>
    </Card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';

import Card from '@C/Card';
import CountrySelect from '@C/CountrySelect';
import Input from '@C/Forms/Input';
import InlineError from '@C/Forms/InlineError';
import FlashMessage from '@C/FlashMessage';
import AutoCompleteInput from '@C/AutoCompleteInput';

import validator from '@M/validationWrapper';

import { dynamicFieldMatching } from '@H/dynamicFieldMatching';

import { emptyOrStringValidator } from '@H/validation';

export default {
    name: 'AddressDetails',
    mixins: [validator],
    props: {
        namespace: {
            type: String,
            required: true
        },
        setCountry: {
            type: Function,
            required: true
        },
        homeCountry: {
            validator: emptyOrStringValidator,
            required: true
        },
        homeHouseNumber: {
            validator: emptyOrStringValidator,
            required: true
        },
        homeHouseName: {
            validator: emptyOrStringValidator,
            required: true
        },
        homeStreet: {
            validator: emptyOrStringValidator,
            required: true
        },
        countries: {
            type: Array,
            required: true
        },
        predictions: {
            type: Array,
            required: true
        },
        emulatePlaceChange: {
            type: Function,
            required: true
        },
        getIsoCodeFromName: {
            type: Function,
            required: true
        },
        callAutoComplete: {
            type: Function,
            required: true
        }
    },
    computed: {
        ...mapState(['formSubmitting', 'bus']),
        ...dynamicFieldMatching('namespace', [
            'user.home_state',
            'user.home_city',
            'user.home_postcode'
        ]),
        showPostcode: function () {
            return (
                this.homeCountry == 'United Kingdom' ||
                typeof this.homeCountry == 'undefined'
            );
        },
        showCounty() {
            return this.homeCountry == 'United Kingdom';
        },
        showState() {
            return (
                this.homeCountry == 'United States' ||
                this.homeCountry == 'United Kingdom'
            );
        }
    },
    methods: {
        ...mapActions({
            getOrganisedCountries: 'getOrganisedCountries',
        }),
        emitBusChange(e, key) {
            this.bus.$emit('update:detail', { value: e.target.value, key });
        },
        submitForm() {
            this.$refs.form.dispatchEvent(
                new Event('submit', { cancelable: true })
            );
        },
        countryHandler(e, firstRun = false) {
            const val = e.value || e.target.value;
            this.getIsoCodeFromName(val, this.countries);
            this.setCountry(val);

            if (!firstRun) {
                this.resetHomeFields();
                this.$nextTick(() => {
                    this.validate();
                });
            }
        }
    },
    components: {
        Card,
        CountrySelect,
        Input,
        InlineError,
        FlashMessage,
        AutoCompleteInput
    },
    mixins: [validator]
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/Enrolment/AddressDetails.vue